import React, { useEffect, useRef, useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
  Box
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AuthenticatedLayout from '@screens/Authenticated/Layout/Layout';
import { useUserStore } from '@stores/User/User';
import { maskPhone } from '@utils/masks';

const sectionTitleSx = {
  color: '#105330',
  fontWeight: 'bold',
  fontSize: '1rem',
  mb: 2,
  mt: 1
};

const submitBtnSx = {
  backgroundColor: '#105330',
  '&:hover': { backgroundColor: '#2FB674' },
  fontWeight: 'bold',
  minWidth: 160
};

const AccountScreen: React.FC = () => {
  const { user, updateUserAvatar, updateOwnProfile, changePassword } =
    useUserStore();

  // ─── Avatar ───────────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  const handleAvatarClick = () => fileInputRef.current?.click();
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showError('Selecione um arquivo de imagem válido.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError('O arquivo deve ter no máximo 5 MB.');
      return;
    }
    setAvatarLoading(true);
    const ok = await updateUserAvatar(file);
    setAvatarLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    ok
      ? showSuccess('Avatar atualizado com sucesso!')
      : showError('Erro ao atualizar o avatar.');
  };

  // ─── Celular ──────────────────────────────────────────────────────────────
  const [cellphone, setCellphone] = useState(maskPhone(user?.cellphone ?? ''));
  const [cellLoading, setCellLoading] = useState(false);

  // Sync input whenever the store user changes (e.g. after save or re-login)
  useEffect(() => {
    setCellphone(maskPhone(user?.cellphone ?? ''));
  }, [user?.cellphone]);

  const handleCellSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCellLoading(true);
    const ok = await updateOwnProfile({ cellphone: cellphone || undefined });
    setCellLoading(false);
    ok
      ? showSuccess('Celular atualizado!')
      : showError('Erro ao atualizar o celular.');
  };

  // ─── Senha ────────────────────────────────────────────────────────────────
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');

  const handlePwdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError('');
    if (newPwd.length < 8) {
      setPwdError('A nova senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdError('A confirmação de senha não confere.');
      return;
    }
    setPwdLoading(true);
    const result = await changePassword(currentPwd, newPwd);
    setPwdLoading(false);
    if (result.ok) {
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
      showSuccess('Senha alterada com sucesso!');
    } else {
      setPwdError(result.message ?? 'Erro ao alterar senha.');
    }
  };

  // ─── Snackbar helpers ─────────────────────────────────────────────────────
  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error';
  }>({ open: false, msg: '', sev: 'success' });

  const showSuccess = (msg: string) =>
    setSnack({ open: true, msg, sev: 'success' });
  const showError = (msg: string) =>
    setSnack({ open: true, msg, sev: 'error' });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <AuthenticatedLayout>
      {/* Header da página */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#105330', fontWeight: 'bold' }}>
          {user?.name ?? 'Minha Conta'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.email}
        </Typography>
        <Divider sx={{ mt: 2 }} />
      </Box>

      {/* ── Avatar ─────────────────────────────────────────────────────── */}
      <Typography sx={sectionTitleSx}>Foto de Perfil</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
        <Box
          onClick={handleAvatarClick}
          sx={{ cursor: 'pointer', position: 'relative' }}>
          <Avatar
            src={user?.avatar ?? undefined}
            sx={{ width: 80, height: 80, bgcolor: '#105330' }}>
            {!user?.avatar && <AccountCircleIcon sx={{ fontSize: 48 }} />}
          </Avatar>
          {avatarLoading && (
            <CircularProgress
              size={80}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                color: '#2FB674'
              }}
            />
          )}
        </Box>
        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            onClick={handleAvatarClick}
            disabled={avatarLoading}
            sx={{
              borderColor: '#105330',
              color: '#105330',
              '&:hover': { borderColor: '#2FB674', color: '#2FB674' }
            }}>
            {avatarLoading ? 'Enviando…' : 'Trocar foto'}
          </Button>
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 0.5 }}>
            JPG, PNG ou GIF • máx. 5 MB
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* ── Celular ─────────────────────────────────────────────────────── */}
      <Typography sx={sectionTitleSx}>Telefone Celular</Typography>
      <Box
        component="form"
        onSubmit={handleCellSubmit}
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          mb: 3,
          flexWrap: 'wrap'
        }}>
        <TextField
          label="Celular"
          value={cellphone}
          onChange={(e) => setCellphone(maskPhone(e.target.value))}
          variant="outlined"
          size="small"
          sx={{ width: 220 }}
          disabled={cellLoading}
          placeholder="(00) 00000-0000"
        />
        <Button
          type="submit"
          variant="contained"
          disabled={cellLoading}
          sx={submitBtnSx}>
          {cellLoading ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            'Salvar celular'
          )}
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* ── Senha ───────────────────────────────────────────────────────── */}
      <Typography sx={sectionTitleSx}>Alterar Senha</Typography>
      <Box
        component="form"
        onSubmit={handlePwdSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxWidth: 360
        }}>
        <TextField
          label="Senha atual"
          type="password"
          value={currentPwd}
          onChange={(e) => setCurrentPwd(e.target.value)}
          variant="outlined"
          size="small"
          required
          disabled={pwdLoading}
          autoComplete="current-password"
        />
        <TextField
          label="Nova senha"
          type="password"
          value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)}
          variant="outlined"
          size="small"
          required
          disabled={pwdLoading}
          autoComplete="new-password"
          helperText="Mínimo 8 caracteres"
        />
        <TextField
          label="Confirmar nova senha"
          type="password"
          value={confirmPwd}
          onChange={(e) => setConfirmPwd(e.target.value)}
          variant="outlined"
          size="small"
          required
          disabled={pwdLoading}
          autoComplete="new-password"
        />
        {!!pwdError && (
          <Typography variant="body2" color="error">
            {pwdError}
          </Typography>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={pwdLoading}
          sx={{ ...submitBtnSx, alignSelf: 'flex-start' }}>
          {pwdLoading ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            'Alterar senha'
          )}
        </Button>
      </Box>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={closeSnack} severity={snack.sev} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </AuthenticatedLayout>
  );
};

export default AccountScreen;
